import React, { useEffect, useState } from "react";

/**
 * Live opening hours + tour pricing for the public site.
 *
 * Both are edited from the admin (web + desktop app) and served by
 * GET /api/opening-hours and GET /api/tours/pricing. Each endpoint is fetched
 * at most once per page load: the first hook call starts the request, every
 * other component shares the same promise/result. Until it lands (or if it
 * fails) the hooks return fallbacks that match the site's long-standing
 * defaults, so nothing flickers or breaks when the backend is unreachable.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const FALLBACK_OPENING_HOURS = {
  hours: "Open daily 8:30am – 5:30pm",
  closed_on_tuesdays: false,
  holiday_hours_notice: false,
  summary: ["Open daily 8:30am – 5:30pm"],
  weekly: DAY_NAMES.map((name, day) => ({
    day,
    name,
    closed: false,
    open: "08:30",
    close: "17:30",
    text: "8:30am – 5:30pm",
  })),
  today: null,
  special_days: [],
  schema_org: {
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: DAY_NAMES.map((d) => `https://schema.org/${d}`),
        opens: "08:30",
        closes: "17:30",
      },
    ],
    specialOpeningHoursSpecification: [],
  },
};

export const FALLBACK_TOUR_PRICING = {
  adult: 16.5,
  child: 9.5,
  family_packages: [],
  custom_packages: [],
};

// ---- shared module-level cache -------------------------------------------

function makeLoader(path, normalize) {
  let promise = null;
  let value = null;
  return {
    get: () => value,
    load: () => {
      if (!promise) {
        promise = fetch(`${API}${path}`)
          .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`${path} ${res.status}`))))
          .then((data) => {
            value = normalize(data);
            return value;
          })
          .catch((err) => {
            console.error(`Error fetching ${path}:`, err);
            return null; // keep the fallback; don't retry within this page load
          });
      }
      return promise;
    },
  };
}

const arr = (v, fallback) => (Array.isArray(v) ? v : fallback);

const hoursLoader = makeLoader("/api/opening-hours", (d) => {
  const summary = arr(d && d.summary, null);
  return {
    ...FALLBACK_OPENING_HOURS,
    ...d,
    summary: summary && summary.length ? summary : FALLBACK_OPENING_HOURS.summary,
    weekly: arr(d && d.weekly, []).length === 7 ? d.weekly : FALLBACK_OPENING_HOURS.weekly,
    special_days: arr(d && d.special_days, []),
    schema_org: {
      openingHoursSpecification: arr(
        d && d.schema_org && d.schema_org.openingHoursSpecification,
        FALLBACK_OPENING_HOURS.schema_org.openingHoursSpecification
      ),
      specialOpeningHoursSpecification: arr(d && d.schema_org && d.schema_org.specialOpeningHoursSpecification, []),
    },
  };
});

const pricingLoader = makeLoader("/api/tours/pricing", (d) => ({
  ...FALLBACK_TOUR_PRICING,
  ...d,
  adult: Number(d && d.adult) > 0 ? Number(d.adult) : FALLBACK_TOUR_PRICING.adult,
  child: Number(d && d.child) > 0 ? Number(d.child) : FALLBACK_TOUR_PRICING.child,
  family_packages: arr(d && d.family_packages, []),
  custom_packages: arr(d && d.custom_packages, []),
}));

function useLoaded(loader, fallback) {
  const [value, setValue] = useState(() => loader.get() || fallback);
  useEffect(() => {
    let alive = true;
    loader.load().then((v) => {
      if (alive && v) setValue(v);
    });
    return () => {
      alive = false;
    };
  }, [loader]);
  return value;
}

/** Opening hours: { summary, weekly, special_days, schema_org, closed_on_tuesdays, ... } */
export function useOpeningHours() {
  return useLoaded(hoursLoader, FALLBACK_OPENING_HOURS);
}

/** Tour pricing: { adult, child, family_packages, custom_packages } */
export function useTourPricing() {
  return useLoaded(pricingLoader, FALLBACK_TOUR_PRICING);
}

// ---- formatting helpers ----------------------------------------------------

/** 16 → "$16", 15.5 → "$15.50"; pass { cents: true } to always show cents. */
export function formatPrice(value, { cents = false } = {}) {
  const n = Number(value) || 0;
  return cents || !Number.isInteger(n) ? `$${n.toFixed(2)}` : `$${n}`;
}

/**
 * The summary as one line for tight spots (header strip, footer):
 * ["Open daily 8:30am – 5:30pm"] → "Open daily · 8:30am – 5:30pm".
 */
export function hoursLine(summary) {
  return (summary || FALLBACK_OPENING_HOURS.summary).join(" · ").replace(/^Open daily /, "Open daily · ");
}

/** "2026-12-25" → "Fri 25 Dec" (calendar date, no timezone shifting). */
export function formatShortDate(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return `${SHORT_DAYS[dt.getDay()]} ${d} ${SHORT_MONTHS[m - 1]}`;
}

/** "Christmas Day (Fri 25 Dec) — Closed" */
export function formatSpecialDay(day) {
  const when = formatShortDate(day.date);
  const text = day.text || (day.closed ? "Closed" : "");
  return `${day.label ? `${day.label} (${when})` : when} — ${text}`;
}

/**
 * Schema.org opening-hours fields to spread into a page's JSON-LD.
 * Omits specialOpeningHoursSpecification when there are none.
 */
export function openingHoursSchema(hours) {
  const so = (hours && hours.schema_org) || FALLBACK_OPENING_HOURS.schema_org;
  const out = { openingHoursSpecification: so.openingHoursSpecification };
  if (so.specialOpeningHoursSpecification && so.specialOpeningHoursSpecification.length) {
    out.specialOpeningHoursSpecification = so.specialOpeningHoursSpecification;
  }
  return out;
}

/**
 * "Upcoming changes" list for special days (holiday closures / altered
 * hours). Renders nothing when there are none. Pass `as`/`className`s to fit
 * the surrounding markup.
 */
export function UpcomingChanges({ specialDays, className, itemClassName, title = "Upcoming changes", inline = false }) {
  if (!specialDays || specialDays.length === 0) return null;
  if (inline) {
    return (
      <span className={className}>
        {title}: {specialDays.map(formatSpecialDay).join(" · ")}
      </span>
    );
  }
  return (
    <div className={className}>
      <div>{title}:</div>
      {specialDays.map((d) => (
        <div key={d.date} className={itemClassName}>
          {formatSpecialDay(d)}
        </div>
      ))}
    </div>
  );
}
